/**
 * EnvContext.tsx
 * Runtime environment switcher for the Analysis API.
 * Both URLs/keys are baked in at build time; the user selects which is active.
 * Selection persists in localStorage across refreshes.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export type EnvName = 'staging' | 'prod';

export interface EnvConfig {
  name: EnvName;
  label: string;
  apiUrl: string;
  apiKey: string;
}

const STORAGE_KEY = 'oc_analysis_env';

const ENVS: Record<EnvName, EnvConfig> = {
  staging: {
    name:   'staging',
    label:  'Staging',
    apiUrl: process.env.REACT_APP_ANALYSIS_API_URL_STAGING ?? process.env.REACT_APP_ANALYSIS_API_URL ?? '',
    apiKey: process.env.REACT_APP_ANALYSIS_API_KEY_STAGING ?? process.env.REACT_APP_ANALYSIS_API_KEY ?? '',
  },
  prod: {
    name:   'prod',
    label:  'Prod',
    apiUrl: process.env.REACT_APP_ANALYSIS_API_URL_PROD ?? process.env.REACT_APP_ANALYSIS_API_URL ?? '',
    apiKey: process.env.REACT_APP_ANALYSIS_API_KEY_PROD ?? process.env.REACT_APP_ANALYSIS_API_KEY ?? '',
  },
};

function readStoredEnv(): EnvName {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'staging' || stored === 'prod') return stored;
  } catch { /* ignore */ }
  return 'staging';
}

interface EnvContextType {
  env: EnvConfig;
  envName: EnvName;
  setEnv: (name: EnvName) => void;
  allEnvs: EnvConfig[];
}

const EnvContext = createContext<EnvContextType>({} as EnvContextType);

export const EnvProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [envName, setEnvName] = useState<EnvName>(readStoredEnv);

  const setEnv = useCallback((name: EnvName) => {
    setEnvName(name);
    try { localStorage.setItem(STORAGE_KEY, name); } catch { /* ignore */ }
  }, []);

  return (
    <EnvContext.Provider value={{
      env:     ENVS[envName],
      envName,
      setEnv,
      allEnvs: Object.values(ENVS),
    }}>
      {children}
    </EnvContext.Provider>
  );
};

export function useEnv(): EnvContextType {
  return useContext(EnvContext);
}

/** Convenience: get active API config without full context */
export function getActiveEnv(): EnvConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as EnvName | null;
    const name: EnvName = (stored === 'staging' || stored === 'prod') ? stored : 'staging';
    return ENVS[name];
  } catch {
    return ENVS['staging'];
  }
}
